import { CircleSubtractIcon, HandIcon, InfinityIcon } from "@xola/icons";
import { Alert, Dot, flash, FormGroup, Input, Label, Modal, SubmitButton } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate, isOtherSellerPurchase, Loader } from "common";
import dayjs from "dayjs";
import { isNumber, sum } from "lodash";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { Except } from "type-fest";
import {
    AllowedPrivacy,
    Event,
    EventCapacityAdjustment,
    OPEN_UNLIMITED,
    Permission,
    PrioritizedResource,
    Purchase,
    ResourceType,
    Timeslot,
} from "types";
import { api } from "../../api/api";
import { useAuthorization } from "../../hooks/useAuthorization";
import { useInvalidate } from "../../hooks/useInvalidation";
import { getStart } from "../../models/Event";
import { getMaxCapacity, getOpenCapacity, getReservedCapacity, hasWaitlists } from "../../models/Timeslot";
import { useDashboardFilterStore } from "../../stores/dashboardFilterStore";
import { DashboardScope } from "../../types/Dashboard";
import { updateCache } from "../../utils/updateCache";
import { IsGranted } from "../Common/IsGranted";
import { QuantitySpinner } from "../Common/QuantitySpinner/QuantitySpinner";
import { SecondaryButton } from "../Common/SecondaryButton";
import { getIncludedFields } from "../Dashboard/Common/timeslotParams";
import { BlackoutEventModal } from "./BlackoutEventModal";
import { CapEventModal } from "./CapEventModal";
import { getBlackoutSchedulePayload } from "./EventSchedulesHelper";
import { EventSummaryTile } from "./EventSummaryTile";
import { DashboardProductTile } from "@/components/Dashboard/Item/DashboardProductTile";
import { useEvent } from "@/hooks/resources/useEvent";
import { useExperience } from "@/hooks/resources/useProduct";
import { useModal } from "@/hooks/useVisibility";
import { getSeller } from "@/utils/getSeller";

interface AdjustCapacityModalProps {
    timeslot: Timeslot;
    isOpen: boolean;
    onClose: () => void;
    onCreatedEvent: (eventId: ID, shouldRedirectToDashboard?: boolean) => void;
}

export const EditEventCapacityModal = ({ isOpen, onClose, timeslot, onCreatedEvent }: AdjustCapacityModalProps) => {
    return (
        <Modal isLoading size="large" isOpen={isOpen} onClose={onClose}>
            <ModalContent timeslot={timeslot} onCreatedEvent={onCreatedEvent} onClose={onClose} />
        </Modal>
    );
};

const ModalContent = ({ timeslot, onClose, onCreatedEvent }: Except<AdjustCapacityModalProps, "isOpen">) => {
    const history = useHistory();
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();

    const scope = useDashboardFilterStore((state) => state.filters.scope);
    const isAvailableScope = scope === DashboardScope.AVAILABLE;

    const { event } = useEvent(timeslot.event?.id, null, { enabled: !!timeslot.event?.id });
    const { experience } = useExperience(timeslot.product);

    const hasEquipment = experience?.resources?.some((item) => item.resource.type === ResourceType.EQUIPMENT);
    const equipments = experience?.resources?.filter((item) => item.resource.type === ResourceType.EQUIPMENT);
    const [{ max, open }, setMaxOpen] = useState({
        max: event?.max ?? getMaxCapacity(timeslot),
        open: event?.open ?? getOpenCapacity(timeslot),
    });
    useEffect(() => {
        setMaxOpen((previousState) => ({
            max: event?.max ?? previousState.max,
            open: event?.open ?? previousState.open,
        }));
    }, [event, event?.max, event?.open]);
    useEffect(() => {
        setMaxOpen({
            max: getMaxCapacity(timeslot),
            open: getOpenCapacity(timeslot),
        });
    }, [timeslot, timeslot?.capacity?.max, timeslot?.capacity?.open]);

    const { mutateAsync: updateEvent, isLoading: updatingEvent } = useMutation(async (payload: EventCapacityUpdate) => {
        try {
            const { data: event } = await api.put<Event>(`/events/${payload.id}`, payload);
            return event;
        } catch {
            return null;
        }
    });

    const { mutateAsync: blackoutEvent, isLoading: blackoutPending } = useMutation(async (event?: Event) => {
        try {
            const payload = getBlackoutSchedulePayload({ timeslot, event, experience });
            return await api.post(`/experiences/${experience!.id}/schedules`, payload);
        } catch {
            return null;
        }
    });

    const [dirtyCapacity, setDirtyCapacity] = useState<number | null>(null);
    const [reason, setReason] = useState("");
    const capEventModal = useModal(false);
    const blackoutEventModal = useModal(false);

    if (!experience) {
        return <Loader />;
    }

    const currentCapacity = max ?? getReservedCapacity(timeslot);
    const capacity = dirtyCapacity ?? currentCapacity;

    const totalEquipmentCapacity = getTotalEquipmentCapacity(equipments);
    const isCurrentCapacityMoreThenEquipmentCapacity = Boolean(
        totalEquipmentCapacity && capacity > totalEquipmentCapacity,
    );

    const available = getAvailableGuestCount(capacity, currentCapacity, timeslot, equipments, open);

    const invalidateData = (event?: Event) => {
        // give some time delay for elastic search to get updated and invalidate to get new data
        setTimeout(async () => {
            const updateTimeslot = {
                ...timeslot,
                canBook: event && Boolean(event?.open > 0),
                capacity: { ...timeslot.capacity, open: event?.open, max: event?.max },
            };

            updateCache("timeslots", updateTimeslot, true, timeslot.id);
        }, 3000);

        if (timeslot.event?.id ?? event?.id) {
            invalidate("events", timeslot.event?.id ?? event?.id);
        }
    };

    const onCapEvent = async () => {
        const eventData = event
            ? await updateEvent({
                  ...event,
                  max: event.quantity.reserved,
                  capacityAdjustment: getCapacityAdjustment(reason),
              })
            : await createEvent(timeslot, timeslot.capacity.reserved, reason);

        if (eventData) {
            flash.success(`Event capped to ${eventData.quantity.reserved ?? timeslot.capacity.reserved} seats`);
        }

        if (eventData) {
            invalidateData(eventData);
        }

        capEventModal.toggle();

        if (!event && eventData) {
            // Redirect to dashboard once event created instead of event roster,
            // in case the `Available` view is open and slot is capped.
            onCreatedEvent(eventData.id, isAvailableScope);
        } else if (isAvailableScope) {
            // In the `Available` view.
            // Once event is capped and available slots become 0,
            // we need to remove timeslot from the list and open dashboard.
            history.push("/dashboard");
            invalidate("events");
            invalidate("timeslots");
        }

        onClose();
    };

    const onBlackoutEvent = async () => {
        await blackoutEvent(event);
        flash.show({ text: `Event blacked out` });

        blackoutEventModal.toggle();
        invalidateData(event);

        onClose();
    };

    const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value);
    const hasCapacityChange = dirtyCapacity !== null && dirtyCapacity !== currentCapacity;
    const hasReason = reason.trim() !== "";

    const onApplyChanges = async () => {
        const eventData = event
            ? await updateEvent({ ...event, max: capacity, capacityAdjustment: getCapacityAdjustment(reason) })
            : await createEvent(timeslot, capacity, reason);

        if (hasCapacityChange && eventData?.max && dirtyCapacity !== eventData.max) {
            flash.show({
                text: `Availability adjusted to ${eventData?.max}/${capacity} based on factors like per event maximum and available equipment capacity`,
            });
        }

        if (!event && eventData) {
            onCreatedEvent(eventData.id);
        }

        if (eventData) {
            invalidateData(eventData);
        }

        onClose();
    };

    return (
        <>
            <Modal.Header>Change Capacity</Modal.Header>
            <Modal.Body>
                <div className="space-y-6">
                    {event ? (
                        <EventSummaryTile event={event} quantity={timeslot.capacity.reserved} className="mb-0" />
                    ) : (
                        <DashboardProductTile timeslot={timeslot} experience={experience} />
                    )}

                    <Alert color="warning">
                        <span>
                            Actual availability will be adjusted based on per event maximum capacity{" "}
                            {hasEquipment ? "and available equipment capacity" : ""}
                        </span>
                    </Alert>

                    <div className="flex items-center justify-center space-x-2">
                        <span
                            className={clsx("font-bold", available < 0 && "text-danger")}
                            data-testid="edit-event-capacity-modal-available"
                        >
                            <var data-var="availability">
                                {available >= OPEN_UNLIMITED ? <InfinityIcon /> : available}
                            </var>
                            &nbsp;Available
                        </span>

                        <Dot color="secondary" className="inline-block align-middle" />

                        <span className="font-bold" data-testid="edit-event-capacity-modal-reserved">
                            <var data-var="reservedCount">{timeslot.capacity.reserved}</var> Reserved
                        </span>

                        <IsGranted permissions={Permission.WAITLISTS_VIEW}>
                            {hasWaitlists(timeslot) ? (
                                <>
                                    <Dot color="secondary" className="inline-block align-middle" />

                                    <span
                                        className="font-bold"
                                        data-testid="edit-event-capacity-modal-waitlisted-count"
                                    >
                                        <var data-var="waitlistCount">{timeslot.waitlistCount ?? 0}</var> Waitlisted
                                    </span>
                                </>
                            ) : null}
                        </IsGranted>
                    </div>

                    <IsGranted permissions={Permission.MODIFY_BOOKING}>
                        {available < OPEN_UNLIMITED && (
                            <div className="flex flex-col items-center justify-center space-y-6 border-t border-gray-light pt-6">
                                <QuantitySpinner
                                    size="large"
                                    value={capacity}
                                    text="Capacity"
                                    classNames={{ label: "text-lg" }}
                                    onChange={setDirtyCapacity}
                                />
                                {isCurrentCapacityMoreThenEquipmentCapacity && (
                                    <Alert color="warning">
                                        <span>Capacity is now greater than available equipment</span>
                                    </Alert>
                                )}
                            </div>
                        )}
                    </IsGranted>
                </div>

                <IsGranted permissions={Permission.MODIFY_BOOKING}>
                    <div className="mt-6 flex space-x-4 border-b border-gray-light pb-8">
                        <SecondaryButton
                            className="flex-grow"
                            icon={<HandIcon size="small" />}
                            disabled={event ? event.max === 0 : timeslot.capacity.max === 0}
                            data-testid="edit-event-capacity-modal-toggle-cap-event"
                            onClick={capEventModal.toggle}
                        >
                            Cap event
                        </SecondaryButton>
                        <SecondaryButton
                            className="flex-grow"
                            icon={<CircleSubtractIcon size="small" />}
                            disabled={!isGranted(Permission.MODIFY_EQUIPMENT_CAPACITY_BLACKOUT)}
                            data-testid="edit-event-capacity-modal-toggle-blackout-event"
                            onClick={blackoutEventModal.toggle}
                        >
                            Blackout
                        </SecondaryButton>
                    </div>

                    <FormGroup className="mt-6">
                        <Label>Reason</Label>
                        <Input
                            value={reason}
                            placeholder="Reason"
                            data-testid="edit-event-capacity-modal-reason"
                            onChange={handleReasonChange}
                        />
                    </FormGroup>
                </IsGranted>
            </Modal.Body>

            <Modal.Footer>
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                <SubmitButton
                    isLoading={updatingEvent}
                    disabled={(!hasCapacityChange && !hasReason) || isCurrentCapacityMoreThenEquipmentCapacity}
                    onClick={onApplyChanges}
                >
                    Apply Changes
                </SubmitButton>
            </Modal.Footer>

            <CapEventModal
                eventCapacity={timeslot.capacity.reserved}
                isOpen={capEventModal.isVisible}
                isLoading={updatingEvent}
                onClose={capEventModal.toggle}
                onConfirm={onCapEvent}
            />
            <BlackoutEventModal
                timeslot={timeslot}
                event={event}
                isOpen={blackoutEventModal.isVisible}
                isLoading={blackoutPending}
                onClose={blackoutEventModal.toggle}
                onConfirm={onBlackoutEvent}
            />
        </>
    );
};

const getAvailableGuestCount = (
    capacity: number,
    currentCapacity: number,
    timeslot: Timeslot,
    equipments: PrioritizedResource[] | undefined,
    open: number,
) => {
    /**
     * We show the availability:
     * - if the capacity is unlimited - show unlimited
     * - if the capacity is limited - show timeslot.capacity.open plus the difference between the current capacity and the total capacity
     * - for private timeslots, if capacity is limited, we show limited capacity amount, decreasing capacity in this case should not update availability unless capacity is reduced
     * is more than the reserved count, if capacity is unlimited, we show reserved count as the capacity.
     */

    let localAvailable = 0;
    if (timeslot.privacy === AllowedPrivacy.PRIVATE && open === 0) {
        localAvailable = capacity > timeslot.capacity.reserved ? 0 : capacity - timeslot.capacity.reserved;
    } else {
        localAvailable = capacity >= OPEN_UNLIMITED ? OPEN_UNLIMITED : open + capacity - currentCapacity;
    }

    const totalEquipmentCapacity = getTotalEquipmentCapacity(equipments);
    const isCurrentCapacityMoreThenEquipmentCapacity = Boolean(
        totalEquipmentCapacity && capacity > totalEquipmentCapacity,
    );

    if (isCurrentCapacityMoreThenEquipmentCapacity) {
        const maxAvailableCapacity = totalEquipmentCapacity - timeslot.capacity.reserved;
        if (timeslot.privacy === AllowedPrivacy.PRIVATE && timeslot.capacity.open === 0) {
            localAvailable = capacity > timeslot.capacity.reserved ? 0 : maxAvailableCapacity;
        } else {
            localAvailable = maxAvailableCapacity;
        }
    }

    return localAvailable;
};

const getTotalEquipmentCapacity = (equipments: PrioritizedResource[] | undefined) => {
    const equipmentTotalCapacity = equipments?.map(
        (equipment) => equipment.resource.capacity * equipment.resource.count ?? 1,
    );

    return sum(equipmentTotalCapacity) ?? 0;
};

export const fetchTimeslot = async (
    timeslot: Timeslot | null,
    event: Event | null,
    productId: ID | null,
    startDate: string | null,
    isGranted: (permission: Permission) => boolean,
) => {
    const isEventBased = !!(timeslot ?? event);
    const eventBasedDate = timeslot?.arrivalDatetime ?? (event?.start ? getStart(event) : dayjs());

    const start = isEventBased ? eventBasedDate : dayjs(startDate);

    const product = isEventBased ? timeslot?.product ?? event?.experience?.id : productId;
    const params = {
        start: `${formatDate(start)},${formatDate(start)}`,
        expand: "event",
        product: product,
        include: getIncludedFields(isGranted),
        limit: 100, // should be more than enough as we search by date and product
    };

    // We can't fetch timeslots by id, so we need to fetch all timeslots for the product and date and filter them
    const { data } = await api.get<{ data: Timeslot[] }>("/timeslots", { params });

    return isEventBased
        ? data.data.find((slot) => {
              if (timeslot) {
                  return slot.id.startsWith(timeslot?.id);
              }

              return slot.event?.id === event?.id;
          })
        : data.data;
};

export interface TimeslotParams {
    timeslot: Timeslot | null;
    event: Event | null;
    productId: ID | null;
    startDate: string | null;
}

export interface UpdateParams {
    isGranted: (permission: Permission) => boolean;
    forceReplaceId?: ID;
    shouldInsertToCache?: boolean;
}

export const updateTimeslotCache = async (timeslotParams: TimeslotParams, updateParams: UpdateParams) => {
    const { timeslot, event, productId, startDate } = timeslotParams;
    const { isGranted, forceReplaceId, shouldInsertToCache } = updateParams;

    const updatedTimeslot = await fetchTimeslot(timeslot, event, productId, startDate, isGranted);

    if (updatedTimeslot) {
        updateCache("timeslots", updatedTimeslot, false, forceReplaceId, shouldInsertToCache);
        return updatedTimeslot;
    }

    flash.danger("Error fetching updated time slot information. Please refresh the app.");
};

export const updatePurchaseTimeslotCache = async (
    purchase: Purchase,
    isGranted: (permission: Permission) => boolean,
) => {
    if (isOtherSellerPurchase(purchase, getSeller().id)) {
        return;
    }

    for (const item of purchase.items) {
        const { data } = await api.get<Event>(`/events/${item.event.id}`);

        const timeslotParams = {
            timeslot: null,
            event: data,
            productId: null,
            startDate: null,
        };

        const updateParams = {
            isGranted: isGranted,
        };

        await updateTimeslotCache(timeslotParams, updateParams);
    }
};

export interface EventCapacityUpdate extends Except<Event, "capacityAdjustment"> {
    capacityAdjustment: Pick<EventCapacityAdjustment, "reason">;
}

export const getCapacityAdjustment = (reason: string) => ({ reason: reason.trim() || undefined });

export const createEvent = async (timeslot: Timeslot, capacity?: number, reason = "") => {
    const max = isNumber(capacity) ? capacity : timeslot.capacity.max < OPEN_UNLIMITED ? timeslot.capacity.max : null;

    const { data } = await api.post<Event>("/events", {
        max,
        open: timeslot.capacity.open,
        start: timeslot.arrivalDatetime.format(DateFormat.DATE_TIME_UTC_ISO),
        end: timeslot.end?.format(DateFormat.DATE_TIME_UTC_ISO),
        experience: { id: timeslot.product },
        capacityAdjustment: getCapacityAdjustment(reason),
    });

    return data;
};
